import React, { useEffect, useRef } from 'react';

// Lightweight starfield/particle background (GPU-friendly)
export default function ParticleLayer({ density = 0.0005, speed = 0.8, color = 'rgba(190, 249, 255, 0.45)' }) {
  const canvasRef = useRef(null);
  const animationRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let width = canvas.width = canvas.offsetWidth;
    let height = canvas.height = canvas.offsetHeight;

    const num = Math.max(50, Math.floor(width * height * density));
    const stars = new Array(num).fill(0).map(() => ({
      x: Math.random() * width,
      y: Math.random() * height,
      z: Math.random() * 0.2 + 0.4,
      r: Math.random() * 1.3 + 0.03,
      vx: (Math.random() - 0.62) * speed,
      vy: (Math.random() - 0.5) * speed,
      phase: Math.random() * Math.PI * 2,
      twinkleSpeed: Math.random() * 0.025 + 0.01,
      twinkleAmp: Math.random() * 0.25 + 0.15,
    }));

    let lastTs = performance.now();
    let t = 0;
    const draw = () => {
      const now = performance.now();
      const dt = Math.min(2.5, (now - lastTs) / 16.67);
      lastTs = now;
      t += dt;

      ctx.clearRect(0, 0, width, height);

      // Gentle global drift gives the scene consistent movement.
      const driftX = Math.sin(t * 0.006) * 0.12;
      const driftY = Math.cos(t * 0.004) * 0.08;

      for (const s of stars) {
        s.x += (s.vx + driftX * s.z) * dt;
        s.y += (s.vy + driftY * s.z) * dt;
        if (s.x < -5) s.x = width + 5; if (s.x > width + 5) s.x = -5;
        if (s.y < -5) s.y = height + 5; if (s.y > height + 5) s.y = -5;

        const twinkle = 1 + Math.sin(t * s.twinkleSpeed + s.phase) * s.twinkleAmp;
        const radius = s.r * 3 * s.z * twinkle;

        const grad = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.r * 4);
        grad.addColorStop(0, color);
        grad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(s.x, s.y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
      animationRef.current = requestAnimationFrame(draw);
    };

    const onResize = () => {
      width = canvas.width = canvas.offsetWidth;
      height = canvas.height = canvas.offsetHeight;
    };

    draw();
    window.addEventListener('resize', onResize);
    return () => { cancelAnimationFrame(animationRef.current); window.removeEventListener('resize', onResize); };
  }, [density, speed, color]);

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
      <div style={{ position:'absolute', inset:0, background: 'radial-gradient(1200px 400px at 50% 20%, rgba(0,229,255,0.04), transparent)'}} />
    </div>
  );
}